<template>
  <div class="hotel-page">
    <PropertyPage @on-availability-update="getRooms" :key="data?.id">
      <template #headline-prefix>
        <div class="hotel-page__breadcrumbs" v-if="breadcrumbs.length && route.query.ibe_mode !== 'true'">
          <Breadcrumbs :breadcrumbs="breadcrumbs" />
        </div>
      </template>
    </PropertyPage>
  </div>
</template>

<script setup lang="ts">
  import {imageUrlWithProxy} from "~/utils/imageproxy";
  import { useHreflangTags } from '~/composables/useSeo';

  const { $siteConfig } = useNuxtApp()
  import { defineAsyncComponent, nextTick } from 'vue'
  const PropertyPage = defineAsyncComponent(() => import('~/components/PropertyPage/PropertyPage.vue'))
  const Breadcrumbs = defineAsyncComponent(() => import('~/components/Breadcrumbs.vue'))
  import { usePropertyStore } from '~/store/property'
  import { useSearchStore } from '~/store/search'
  import { useAccountStore } from "~/store/account";
  import dayjs from '~/utils/dayjs'
  import { scrollToElement } from '~/utils/common'
  const route = useRoute()
  const api = useApi()

  const config = useRuntimeConfig()
  const configData = config.public
  // Forward real User-Agent during SSR so the backend can detect Google bots
  const ssrHeaders = process.server ? useRequestHeaders(['user-agent']) : {}
  const originalUserAgent = ssrHeaders['user-agent'] || ''

  // stores
  const propertyStore = usePropertyStore()
  const searchStore = useSearchStore()
  const accountStore = useAccountStore()
  const slug = <string>route.params.hotelSlug
  const citySlug = <string>route.params.city
  const urlPropertyId = Number(slug.split('-')[0])
  const data = ref<any>(null)
  // Use the resolved hotel ID from API data (handles main hotel redirects)
  // Falls back to URL-extracted ID if data hasn't loaded yet
  const propertyId = computed(() => data.value?.id || urlPropertyId)

  const currencyCookie = useCookie('currency')
  // Signed PPC campaign token: on the SSR first hit only the ptok query param
  // exists (the capture plugin is client-only); after hydration the cookie takes over.
  const ppcTokenCookie = useCookie('_tripening_ppc')

  // Honor the currency the visitor arrived with (Google Hotel Ads passes ?currency= /
  // google-displayed-currency, e.g. SGD) as the session currency, so the whole flow — rooms,
  // booking box, checkout and the created order — renders in that one currency and matches the
  // price Google showed. Only adopt currencies the API actually supports, otherwise every request
  // would 400. Set BEFORE fetchRooms so the X-TRIPENING-CURRENCY header carries it.
  const urlCurrency = String(route.query?.['currency'] || route.query?.['google-displayed-currency'] || '').toUpperCase()
  if (urlCurrency && isSupportedCurrency(urlCurrency) && currencyCookie.value !== urlCurrency) {
    currencyCookie.value = urlCurrency
    accountStore.setCurrency(currencyInfo(urlCurrency))
  }

  // Fetch hotel data during SSR for fast initial render and SEO
  const { data: fetchedData, error: fetchedError } = await useAsyncData(`get-property-${urlPropertyId}`, fetchData)

  // useAsyncData CAPTURES a handler throw into `error` instead of propagating it, so the
  // createError() raised in fetchData() alone was not enough: the page kept rendering with
  // null data and returned HTTP 200 with an empty shell (a soft 404 to crawlers). Surfacing it
  // here, in the setup body, is what actually renders the error page and sets the status.
  if (fetchedError.value) {
    showError(fetchedError.value)
  }

  if (fetchedData.value) {
    data.value = fetchedData.value
  }

  // For search bots: blocking SSR so rooms + structured data are in HTML
  // For regular users: non-blocking (lazy) so page loads fast, rooms load after
  const isGoogleBot = route.query.isgoogle === 'true'
  const requestHeaders = useRequestHeaders(['user-agent'])
  const isSearchBot = isGoogleBot || /googlebot|adsbot-google|google-hoteladsverifier/i.test(requestHeaders['user-agent'] || '')
  const hasDateParams = !!(route.query?.['date_start'] || route.query?.['checkin-date'])

  // A paid click must never land on a page with no prices. Ad final URLs are
  // deliberately dateless — a date baked into an ad goes stale and can promise a
  // sold-out window (2026-08-23: 3 of 14 landings had zero rooms on a frozen
  // Labor Day weekend) — but without dates the room fetch bails and the visitor
  // gets a date picker instead of a rate. Six paid clicks produced zero carts.
  //
  // So the ad URL stays dateless and the DEFAULT is computed here, at click time,
  // which cannot go stale. Bots are excluded: they must keep indexing the
  // dateless page rather than a defaulted price.
  // Near-term first. A large share of paid demand — especially on mobile — is
  // looking for TONIGHT, so the default check-in is today, not a stay a fortnight
  // out. The previous fixed +14/2n default also had a second failure mode: a fixed
  // lead time can land inside a property-specific dead zone (2026-08-24: The Whaler
  // was sold out for the whole Sep 7-23 band, which is exactly where +14 fell, while
  // being sellable from +1). So this is a LADDER, not a single guess: take the first
  // rung that actually returns rooms.
  //
  // Ordered near-term first; the last rung exists so a property that is genuinely
  // full close-in still shows a price rather than an empty page.
  //
  // Deliberately only four rungs. Each rung is a live supplier call, and a visitor
  // who just clicked a paid ad will not wait through six of them — bounded at four,
  // the worst case stays tolerable while the common case (sellable tonight: 9 of 9
  // live PPC properties, 15 of 20 scale candidates on 2026-08-24) costs one call.
  const PAID_DEFAULT_LADDER: [number, number][] = [
    [0, 1],   // tonight
    [1, 1],   // tomorrow
    [3, 1],   // this week
    [14, 2],  // fallback: the old fixed default
  ]
  const paidLadderDates = (lead: number, nights: number) => ({
    date_start: dayjs().add(lead, 'day').format('YYYY-MM-DD'),
    date_end: dayjs().add(lead + nights, 'day').format('YYYY-MM-DD'),
  })
  const isPaidVisit = !!(route.query?.ptok || route.query?.utm_medium === 'cpc')
  // The first rung, used to gate the loading state and as the fallback if every
  // rung comes back empty. fetchRooms may resolve to a later rung.
  const paidLadderRung = (i: number) => {
    const rung: [number, number] = PAID_DEFAULT_LADDER[i] ?? [0, 1]
    return paidLadderDates(rung[0], rung[1])
  }
  const paidDefaultDates = computed(() => {
    if (hasDateParams || isSearchBot || !isPaidVisit) return null
    return paidLadderRung(0)
  })
  // The rung fetchRooms actually landed on — this is what gets written to the URL,
  // so the picker and add-to-cart agree with the prices on screen.
  const resolvedPaidDates = ref<{ date_start: string; date_end: string } | null>(null)

  // Set loading state before fetch starts (for non-bot users with dates)
  if (!isSearchBot && (hasDateParams || paidDefaultDates.value)) {
    searchStore.setLoadingRooms(true)
  }

  const { data: fetchedRooms, status: roomsStatus } = await useAsyncData(`get-rooms-${urlPropertyId}`, fetchRooms, {
    lazy: !isSearchBot,
    server: isSearchBot  // Blocking SSR for bots so structured data is in HTML
  })

  // Redirect to canonical URL AFTER data is set and rooms fetch is set up.
  // Must be outside fetchData() because navigateTo() can throw during SSR,
  // which would cause fetchData to return null and lose the hotel data.
  // Skip redirect for bots — rooms are already in the HTML from blocking SSR,
  // and the 301 would trigger a second SSR render with another room fetch.
  if (data.value?.id && !isSearchBot) {
    await redirect(data.value.id, data.value.geo_place?.slug, data.value.slug)
  }

  // Put the defaulted dates in the URL so the picker, the booking block and the
  // add-to-cart call all agree with the prices on screen. Client-only, because
  // navigateTo during SSR can throw and would lose the hotel data (same reason
  // the canonical redirect above sits outside fetchData).
  //
  // This waits for the ladder to resolve rather than writing the first rung
  // immediately: the visitor may end up on +1 or +3, and the URL has to name the
  // window the displayed prices actually came from. Runs once — with the dates
  // present, paidDefaultDates becomes null on the next evaluation.
  if (process.client && paidDefaultDates.value) {
    watch(
      resolvedPaidDates,
      async (resolved) => {
        if (!resolved) return
        await navigateTo(
          { path: route.path, query: { ...route.query, ...resolved } },
          { replace: true }
        )
      },
      { immediate: true }
    )
  }

  // Sync fetched rooms to property store
  // Response can be: array (legacy) or {rooms: [...], booking_mode: string|null} (new)
  watch([fetchedRooms, roomsStatus], ([data, status]) => {
    if (status === 'success' && data) {
      const roomsArray = Array.isArray(data) ? data : (data.rooms || [])
      const bookingMode = Array.isArray(data) ? null : (data.booking_mode || null)
      const processedRooms = roomsArray.map((room: any) => ({
        ...room,
        images: room.images?.length
          ? room.images.filter((image: any) => !!image.url)
          : room.images
      }))
      propertyStore.clearSelectedRate()
      propertyStore.setRooms(processedRooms)
      propertyStore.setBookingMode(bookingMode)
      searchStore.setSoldOutRooms(processedRooms.length === 0)
      if (processedRooms.length) {
        propertyStore.setBookDirectBtnVisible(true)
        // Scroll to rooms after they load — only on client (document doesn't exist during SSR)
        if (import.meta.client) {
          nextTick(() => {
            setTimeout(() => {
              const propertyRoomsElement = document.querySelector('#propertyRooms')
              if (propertyRoomsElement) {
                scrollToElement('#propertyRooms', -100)
              }
            })
          })
        }
      }
      searchStore.setLoadingRooms(false)
    } else if (status === 'error') {
      searchStore.setLoadingRooms(false)
    }
  }, { immediate: true })

  // computed
  const isBookDirectBtn = computed({
    get() {
      return propertyStore.isBookDirectBtn
    },
    set(newValue: boolean) {
      propertyStore.setBookDirectBtnVisible(newValue)
    }
  })

  const siteHost = computed(() => {
    return configData.siteName || ''
  })

  const breadcrumbs = computed(() => {
    const items: Array<{label: string, url?: string}> = [
      { label: 'Home', url: '/' },
    ]

    const countryName = data.value?.geo_place?.country_name
    const countryCode = data.value?.geo_place?.country_code
    if (countryName && countryCode) {
      items.push({ label: countryName, url: `/hotels/destinations/${countryCode.toLowerCase()}` })
    }

    const cityName = data.value?.geo_place?.name || searchStore.selectedCity?.name
    items.push({ label: cityName || 'Hotels', url: `/hotels/${route.params.city || 'results'}` })

    if (data.value?.name) {
      items.push({ label: data.value.name })
    }

    return items
  })


  const cheapestRate = computed(() => {
    return propertyStore.rooms?.[0]?.rates?.[0]
  })

  const addMobileParameter = (params: any) => {
    let _params = {...params}
    const _query = {...route.query}
    const displayedPrice = _query['google-displayed-price']
    const displayedPriceCurrency =_query['google-displayed-currency']
    // Check both dash and underscore versions of the parameter (Google sends rate-rule-id)
    const rateRuleId = _query['rate-rule-id'] || _query['rate_rule_id']
    const isMobileRateRule = rateRuleId === 'mobile'
    const isMobileDevice = checkIsMobileByUserAgent()

    if ((displayedPrice && rateRuleId) || isMobileRateRule || isMobileDevice) {
      (_params as any)['is_mobile'] = true
    }

    if (displayedPrice) {
      _params['desirable_price'] = Number(displayedPrice).toFixed(2)
    }

    if (displayedPriceCurrency) {
      _params['desirable_price_currency'] = displayedPriceCurrency
    }

    return _params
    function checkIsMobileByUserAgent() {
      if (typeof navigator !== 'undefined' && navigator?.userAgent) {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      }
      return false
    }
  }

  let params = {}

  params = addMobileParameter(params)

  const roomsPayload = {
    criteria: searchStore.criteria,
    ...params
  }

  async function redirect(id: number, cSlug: string, hotelSlug: string) {
    const trueSlug = `${id}-${hotelSlug}`
    const slug = route.params.hotelSlug !== trueSlug ? trueSlug : route.params.hotelSlug
    const url = `/hotels/${cSlug || 'results'}/${slug}`
    const cityCheck = !(citySlug === 'results' || citySlug === cSlug)

    if (route.params.hotelSlug !== trueSlug || cityCheck) {
      await navigateTo({ path: url, query: route.query }, { redirectCode: 301 })
    }
  }

  async function fetchRooms() {
    const explicit_start = route.query?.['date_start'] || route.query?.['checkin-date']
    const explicit_end = route.query?.['date_end'] || route.query?.['checkout-date']
    // Only a paid visit with no dates of its own walks the ladder; everyone else
    // (and every bot) keeps exactly the window they asked for.
    const useLadder = !explicit_start && !!paidDefaultDates.value
    const date_start = explicit_start || paidDefaultDates.value?.date_start
    const date_end = explicit_end || paidDefaultDates.value?.date_end

    if (!date_start || !date_end) {
      return null
    }

    const displayedPrice = route.query?.['google-displayed-price']
    const displayedPriceCurrency = route.query?.['google-displayed-currency']
    // Check both dash and underscore versions of the parameter (Google sends rate-rule-id)
    const rateRuleId = route.query?.['rate-rule-id'] || route.query?.['rate_rule_id']
    const isMobileRateRule = rateRuleId === 'mobile'
    const isMobileDevice = typeof navigator !== 'undefined' && navigator?.userAgent
      ? /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      : false

    let params: any = {}

    // Set is_mobile if: Google requests mobile rate, or user agent is mobile device
    if ((displayedPrice && rateRuleId) || isMobileRateRule || isMobileDevice) {
      params['is_mobile'] = true
    }

    if (displayedPrice) {
      params['desirable_price'] = Number(displayedPrice).toFixed(2)
    }

    if (displayedPriceCurrency) {
      params['desirable_price_currency'] = displayedPriceCurrency
    }

    const buildCriteria = (start: string, end: string) => ({
      adults: Number(route.query.adults) || 2,
      children: Number(route.query.children) || 0,
      ages: route.query.ages ? String(route.query.ages)?.split(',').map((age: string) => Number(age)) : [],
      date_start: start,
      date_end: end
    })

    const promoCode = propertyStore.promoCode
    // During SSR, use internal URL (bypasses Cloudflare) if configured; fall back to public URL
    const apiBase = process.server && config.ssrApiBaseUrl ? config.ssrApiBaseUrl : config.public.baseURL

    const requestRoomsFor = (criteria: any) => $fetch(`${apiBase}/api/product/v2/hotels/${propertyId.value}/get-rooms`, {
      method: 'POST',
      // Tight timeout during SSR to prevent blocking the render pipeline;
      // client-side uses no explicit timeout (global fetch-timeout plugin covers it)
      ...(process.server ? { timeout: 15000 } : {}),
      headers: {
        'X-TRIPENING-CONTEXT-ID': accountStore.contextId,
        'X-TRIPENING-CURRENCY': currencyCookie?.value || 'USD',
        // Forward real User-Agent so backend can detect Google bots during SSR
        ...(process.server && originalUserAgent ? { 'X-Original-User-Agent': originalUserAgent } : {}),
        // PPC margin token: query param covers the SSR first hit, cookie covers the rest
        ...((route.query.ptok || ppcTokenCookie?.value)
          ? { 'X-PPC-Campaign': String(route.query.ptok || ppcTokenCookie.value) }
          : {})
      },
      body: { criteria, ...params, ...(promoCode ? { promo_code: promoCode } : {}) }
    })

    // Response is either an array (legacy) or {rooms: [...]}.
    const roomCount = (response: any) => (Array.isArray(response) ? response.length : (response?.rooms?.length || 0))

    // Commit the window the visitor is actually being shown: booking period drives
    // the price maths, resolvedPaidDates drives the URL rewrite.
    const commitWindow = (start: string, end: string) => {
      searchStore.setBookingPeriod(Math.abs(dayjs(end).diff(dayjs(start), 'days')) || 0)
      if (useLadder) resolvedPaidDates.value = { date_start: start, date_end: end }
    }

    const requestRooms = () => requestRoomsFor(buildCriteria(date_start as string, date_end as string))

    try {
      let rooms: any = null

      if (useLadder) {
        // Walk near-term first, stop at the first rung with real inventory. Rungs are
        // tried in order and the loop exits on the first hit, so the common case
        // (property is sellable tonight) costs exactly one request.
        let landed: { date_start: string; date_end: string } | null = null
        for (const [lead, nights] of PAID_DEFAULT_LADDER) {
          const rung = paidLadderDates(lead, nights)
          try {
            const attempt = await requestRoomsFor(buildCriteria(rung.date_start, rung.date_end))
            if (roomCount(attempt)) {
              rooms = attempt
              landed = rung
              break
            }
          } catch (e) {
            // A rung that errors is not a verdict on the next one — keep walking.
            console.error('Ladder rung failed', rung, e)
          }
        }
        // Nothing anywhere on the ladder: keep the first rung so the picker still
        // shows a sensible near-term window rather than an empty one.
        const shown = landed || paidLadderRung(0)
        commitWindow(shown.date_start, shown.date_end)
        return rooms
      }

      commitWindow(date_start as string, date_end as string)
      rooms = await requestRooms()

      // A supplier can answer "no availability" for a request it served moments earlier —
      // LiteAPI returns HTTP 200 with an empty payload often enough that ~5-9% of get-rooms
      // responses come back empty, 91% of them SSR renders. An empty array is indistinguishable
      // here from a genuine sell-out, so on the bot path we would ship a roomless page with no
      // structured price data straight to Google's verifier. Retry once before accepting it.
      // Bots only: this path is blocking SSR for them, while users fetch rooms client-side and
      // already get a re-fetch from the availability watcher.
      if (isSearchBot && !roomCount(rooms)) {
        rooms = await requestRooms()
      }

      return rooms
    } catch (e) {
      console.error('Error fetching rooms:', e)
      return null
    }
  }

  async function fetchData() {
    // navigateTo() must NOT be called from inside an async .catch(): once that callback awaits,
    // the request's Nuxt instance is no longer active, so it threw `[nuxt] instance unavailable`
    // instead of redirecting. The outer catch then swallowed that error and returned null, so a
    // property whose fetch failed rendered a wholly blank page rather than a 404.
    //
    // createError needs no Nuxt instance and produces a real HTTP status. Status is preserved
    // rather than forced to 404: a transient upstream failure used to send the crawler to /404,
    // which risks deindexing a live property. 503 tells it to come back instead.
    let _data: any = null
    try {
      _data = await api(`/api/product/v1/hotels/${urlPropertyId}`)
    } catch (error: any) {
      console.error('Error fetching property:', error)
      const status = error?.statusCode || error?.response?.status
      throw createError({
        statusCode: status === 404 || status === 410 ? 404 : 503,
        statusMessage: status === 404 || status === 410 ? 'Property not found' : 'Property temporarily unavailable',
        fatal: true
      })
    }

    if (_data?.id) {
      propertyStore.setProperty(_data)
    }
    return _data
  }

  async function getRooms() {
    try {
      searchStore.setLoadingRooms(true)
      // Build params fresh from route.query to ensure Google params are included
      const freshParams = addMobileParameter({})
      const promoCode = propertyStore.promoCode
      const response: any = await api(`/api/product/v2/hotels/${propertyId.value}/get-rooms`, {
        method: 'POST',
        body: {
          criteria: searchStore.criteria,
          ...freshParams,
          ...(promoCode ? { promo_code: promoCode } : {}),
        }
      })
      // Handle both old (array) and new (object with rooms + booking_mode) format
      const rooms = Array.isArray(response) ? response : (response?.rooms || [])
      const bookingMode = Array.isArray(response) ? null : (response?.booking_mode || null)
      searchStore.setSoldOutRooms(rooms?.length === 0)
      rooms.forEach((room: any) => {
        if (room.images?.length) {
          room.images = room.images?.filter((image: any) => !!image.url)
        }
      })
      propertyStore.clearSelectedRate()
      propertyStore.setRooms(rooms)
      propertyStore.setBookingMode(bookingMode)
      propertyStore.isDateChanged = false
      const propertyRoomsElement = document.querySelector('#propertyRooms')
      if (propertyRoomsElement) {
        scrollToElement('#propertyRooms', -100)
      }
      if (rooms?.length) {
        isBookDirectBtn.value = true
      }
    } catch (e) {
      console.log(e)
    } finally {
      searchStore.setLoadingRooms(false)
    }
  }

  function checkRouteParams() {
    const date_start = route.query?.['date_start'] || route.query?.['checkin-date']
    const date_end = route.query?.['date_end'] || route.query?.['checkout-date']
    const criteria = {
      adults: Number(route.query.adults) || 2,
      children: Number(route.query.children) || 0,
      ages: route.query.ages ? String(route.query.ages)?.split(',').map((age: string) => Number(age)) : [],
      date_start: date_start || null,
      date_end: date_end || null
    }

    if (date_start && date_end) {
      searchStore.setDates([
        date_start,
        date_end
      ])
      searchStore.setFormattedDates([
        dayjs(String(date_start)).format('ddd, DD MMM'),
        dayjs(String(date_end)).format('ddd, DD MMM')
      ])
    }
    searchStore.setCriteria(criteria)
    searchStore.setQuantity(Number(route.query.quantity) || 1)
  }

  // lifecycle hooks
  onMounted(() => {
    checkRouteParams()
    // Rooms are fetched via useLazyAsyncData - no need to call getRooms() here
    // getRooms() is only called when user changes dates via @on-availability-update
  })

  onBeforeRouteLeave(() => {
    propertyStore.$reset()
  })

  // Add hreflang tags for multi-language SEO
  useHreflangTags()

</script>

<style lang="scss">
.hotel-page__breadcrumbs {
  padding: 16px 0 0 0;
}
</style>
